import { IErrorResponse } from '@common/api.models';
import { useProjectId } from '@config/config.hooks';
import { convertUtcStringToLocalTimezoneString, DISPLAY_DATE_TIME_FILENAME_FORMAT } from '@core/date.services';
import { DownloadFile } from '@file/DownloadFile';
import { IDownloadDesignOrConstructionDrawingsRequest, IFileResponse } from '@file/projectFile/projectFile.models';
import { downloadDesignOrConstructionDrawingsAsync, downloadFile } from '@file/projectFile/projectFile.services';
import {
  Column,
  PaginatedTable,
  createTableCell,
  createTableColumn,
  Grid,
  Icon,
  CircularProgressBar,
  Tooltip,
} from '@gcl/react-core';
import { useMutation } from '@tanstack/react-query';
import { IProjectTransmittalItemDrawing } from '@transmittal/transmittalItem/transmittalItem.models';
import { useFetchTransmittalItemsDrawings } from '@transmittal/transmittalItem/transmittalItem.queries';
import { AxiosError } from 'axios';
import * as React from 'react';
import './MasterScheduleDetailPage.scss';
import { ApiErrorDisplay } from '@core/ApiErrorDisplay';

interface IMasterScheduleDetailDrawingListProps {
  masterScheduleId: number;
}

export const MasterScheduleDetailDrawingList = ({ masterScheduleId }: IMasterScheduleDetailDrawingListProps) => {
  const projectId = useProjectId();

  const { data: drawingData } = useFetchTransmittalItemsDrawings({
    projectId,
    masterScheduleId,
  });

  const columns = React.useMemo(() => {
    return [
      createTableColumn({
        accessor: 'transmittalItem.docNo',
        Header: 'Drawing No.',

        Cell: createTableCell<string, any>(
          (value: any, item: IProjectTransmittalItemDrawing, index: number, cellExtras: { [key: string]: any }) => {
            return <div>{item.transmittalItem.docNo}</div>;
          },
        ),
      } as Column),
      createTableColumn({
        accessor: 'transmittalItem.description',
        Header: 'Drawing Title',
        Cell: createTableCell<string, any>(
          (value: any, item: IProjectTransmittalItemDrawing, index: number, cellExtras: { [key: string]: any }) => {
            return <div>{item.transmittalItem.description}</div>;
          },
        ),
      } as Column),
      createTableColumn({
        accessor: 'id1',
        Header: () => {
          return (
            <>
              <span className="gcl-mr-sm">Latest Design Drawing</span>
              <Tooltip overlay="Export Design Drawing in ZIP">
                <Icon
                  onClick={() => downloadDesignDrawing(true)}
                  className="gcl-fl-lg gcl-clickable gcl-mr-xxs"
                  iconName="FileEarmarkZipFill"
                  data-testid="T1-drawings-export-design-drawing-zip"
                />
              </Tooltip>
              <Tooltip overlay="Export Design Drawing in PDF">
                <Icon
                  onClick={() => downloadDesignDrawing(false)}
                  className="gcl-fl-lg gcl-clickable"
                  iconName="FilePdfFill"
                  data-testid="T1-drawings-export-design-drawing-pdf"
                />
              </Tooltip>
              <Grid
                item
                flex="0 1 30px"
                className="gcl-pl-xs"
                sx={[
                  isLoadingDownloadConstructionDrawing && { visibility: 'visible' },
                  !isLoadingDownloadConstructionDrawing && { display: 'none' },
                ]}
              >
                <CircularProgressBar value={downloadConstructionDrawingProgress} />
              </Grid>
            </>
          );
        },
        Cell: createTableCell<string, any>(
          (value: any, item: IProjectTransmittalItemDrawing, index: number, cellExtras: { [key: string]: any }) => {
            if (!item.latestDesignDrawing?.revisionNo || !item.latestDesignDrawing.fileId) return null;
            return (
              <div className="gcl-gems-t1-detail-drawing-latest-drawing-col">
                <DownloadFile
                  text={item.latestDesignDrawing?.revisionNo}
                  attachmentId={item.latestDesignDrawing?.fileId}
                  projectId={projectId}
                  shouldNotChop
                />
                {item.latestDesignDrawing.dateOfIssue && (
                  <span className="gcl-ml-xs">
                    ({convertUtcStringToLocalTimezoneString(item.latestDesignDrawing.dateOfIssue)})
                  </span>
                )}
              </div>
            );
          },
        ),
      } as Column),
      createTableColumn({
        accessor: 'id2',
        Header: () => {
          return (
            <>
              <span className="gcl-mr-sm">Latest Construction Drawing</span>
              <Tooltip overlay="Export Construction Drawing in ZIP">
                <Icon
                  onClick={() => downloadConstructionDrawing(true)}
                  className="gcl-fl-lg gcl-clickable gcl-mr-xxs"
                  iconName="FileEarmarkZipFill"
                  data-testid="T1-drawings-export-construction-drawing-zip"
                />
              </Tooltip>
              <Tooltip overlay="Export Construction Drawing in PDF">
                <Icon
                  onClick={() => downloadConstructionDrawing(false)}
                  className="gcl-fl-lg gcl-clickable"
                  iconName="FilePdfFill"
                  data-testid="T1-drawings-export-construction-drawing-pdf"
                />
              </Tooltip>
              <Grid
                item
                flex="0 1 30px"
                className="gcl-pl-xs"
                sx={[
                  isLoadingDownloadDesignDrawing && { visibility: 'visible' },
                  !isLoadingDownloadDesignDrawing && { display: 'none' },
                ]}
              >
                <CircularProgressBar value={downloadDesignDrawingProgress} />
              </Grid>
            </>
          );
        },
        // Header: 'Latest Construction Drawing',
        Cell: createTableCell<string, any>(
          (value: any, item: IProjectTransmittalItemDrawing, index: number, cellExtras: { [key: string]: any }) => {
            if (!item.latestConstructionDrawing?.revisionNo || !item.latestConstructionDrawing.fileId) return null;
            return (
              <div className="gcl-gems-t1-detail-drawing-latest-drawing-col">
                <DownloadFile
                  text={item.latestConstructionDrawing.revisionNo ?? item.latestConstructionDrawing.itemId.toString()}
                  attachmentId={item.latestConstructionDrawing.fileId}
                  projectId={projectId}
                />
                {item.latestConstructionDrawing.dateOfIssue && (
                  <span className="gcl-ml-xs">
                    {convertUtcStringToLocalTimezoneString(
                      item.latestConstructionDrawing.dateOfIssue,
                      DISPLAY_DATE_TIME_FILENAME_FORMAT,
                    )}
                  </span>
                )}
              </div>
            );
          },
        ),
      } as Column),
    ];
  }, []);

  const [downloadConstructionDrawingProgress, setDownloadConstructionDrawingProgress] = React.useState(0);
  const [downloadDesignDrawingProgress, setDownloadDesignDrawingProgress] = React.useState(0);
  const mutateDesignOrConsturctionDrawings = (isConstructionDrawing: boolean) => {
    return useMutation<IFileResponse, AxiosError<IErrorResponse>, IDownloadDesignOrConstructionDrawingsRequest, any>(
      downloadDesignOrConstructionDrawingsAsync,
      {
        onError: (error: AxiosError<IErrorResponse>) => {
          if (isConstructionDrawing) setDownloadConstructionDrawingProgress(0);
          else setDownloadDesignDrawingProgress(0);
        },
        onSuccess: (data, variables, context) => {
          if (isConstructionDrawing) setDownloadConstructionDrawingProgress(0);
          else setDownloadDesignDrawingProgress(0);
          downloadFile(data.file, data.filename);
        },
      },
    );
  };

  const {
    mutate: mutateDownloadConstructionDrawing,
    isLoading: isLoadingDownloadConstructionDrawing,
    error: errorDownloadConstructionDrawing,
    reset: resetDownloadConstructionDrawing,
  } = mutateDesignOrConsturctionDrawings(true);
  const {
    mutate: mutateDownloadDesignDrawing,
    isLoading: isLoadingDownloadDesignDrawing,
    error: errorDownloadDesignDrawing,
    reset: resetDownloadDesignDrawing,
  } = mutateDesignOrConsturctionDrawings(false);

  const downloadConstructionDrawing = (isZip: boolean) => {
    mutateDownloadConstructionDrawing({
      projectId,
      masterScheduleId,
      isZip,
      isConstructionDrawing: true,
      onDownloadProgressUpdate: (percentCompleted: number) => {
        setDownloadConstructionDrawingProgress(percentCompleted);
      },
    });
  };
  const downloadDesignDrawing = (isZip: boolean) => {
    mutateDownloadDesignDrawing({
      projectId,
      masterScheduleId,
      isZip,
      isConstructionDrawing: false,
      onDownloadProgressUpdate: (percentCompleted: number) => {
        setDownloadDesignDrawingProgress(percentCompleted);
      },
    });
  };

  return (
    <div>
      <ApiErrorDisplay
        errors={[
          {
            error: errorDownloadConstructionDrawing,
            genericError: 'Failed to download file',
            reset: resetDownloadConstructionDrawing,
          },
          {
            error: errorDownloadDesignDrawing,
            genericError: 'Failed to download file',
            reset: resetDownloadDesignDrawing,
          },
        ]}
      />

      <PaginatedTable allowSelectRow={false} disablePagination items={drawingData ?? []} columns={columns} />
    </div>
  );
};
